/**
 * ARENA — o CHÃO do mundo em TODAS as regiões (10.5S). Antes só a Floresta tinha território,
 * bioma, props e colisão; as Áreas 2 e 3 eram um tapete liso e atravessável. Agora cada região
 * constrói (sob demanda) o seu chão: manchas de bioma por território, árvores/pedras/grama/
 * cristais em InstancedMesh, lagos e troncos, ambiente vivo (nuvens, vento, bichos) e a
 * MURALHA + colisão em grid de buckets. As classes ShatteredWilds/TempestReach seguem cuidando
 * só dos marcos (ruínas, platôs, cristais gigantes) daquela região.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { randRange } from "../utils/math";
import { quality } from "../core/Quality";
import { territoriesForRegion, regionHalfSize, type Territory } from "./Territories";
import { crystalStdMat, energyMat, foliageMat, stoneMat } from "./Materials";
import { groundTextures, decalTexture, type GroundStyle } from "./GroundTextures";
import { applyWorldStone } from "./WorldStone";

/**
 * 10.9GQ — ESTILO DE CHÃO POR REGIÃO: dois conjuntos de textura (albedo + normal) misturados por
 * um peso pintado nos vértices (splat) segundo o bioma do território. A cor continua vindo dos
 * vértices; a textura dá o material (relva, placas de rocha, pedra estriada, gelo, cinza).
 */
const REGION_GROUND: Record<string, { a: GroundStyle; b: GroundStyle; splat: Record<string, number> }> = {
  forgottenForest: { a: "grass", b: "rock", splat: { rocky: 1, crystal: 0.8, den: 0.35, marsh: 0.1 } },
  shatteredWilds: { a: "rock", b: "ash", splat: { ember: 1, fissure: 0.3, shard: 0.15 } },
  tempestReach: { a: "storm", b: "ice", splat: { frost: 1, ruin: 0.35, galewind: 0.15 } },
};

interface Obstacle {
  x: number;
  z: number;
  r: number;
}
interface Flock {
  g: THREE.Group;
  r: number;
  ph: number;
  sp: number;
  cx: number;
  cz: number;
}
/** 10.9BR — esporos/partículas de bioma num InstancedMesh por tipo (antes: 1 Mesh = 1 draw call cada). */
interface SporeBatch {
  im: THREE.InstancedMesh;
  base: Float32Array; // xyz por instância
  ph: Float32Array;
  sp: Float32Array;
  n: number;
  /** 10.9BS — vida por região: material (opacidade dos vagalumes à noite), velocidade LATERAL
   *  (detritos da Tempestade, m/s, com wrap no mapa) e piscar (vagalumes/faíscas). */
  mat: THREE.MeshBasicMaterial;
  vx: number;
  blink: boolean;
  half: number;
  baseOpacity: number;
}
/** 10.9BS — opções extras de um lote de partículas ambiente. */
interface SporeOpts {
  vx?: number;
  blink?: boolean;
}
/** 10.9BR — um pedaço (60×60 m) de decoração instanciada: liga/desliga por distância do player. */
interface Chunk {
  im: THREE.InstancedMesh;
  cx: number;
  cz: number;
}
/** Tudo que pertence ao chão de UMA região. */
interface RegionBuild {
  group: THREE.Group;
  /** 10.6H — cada região tem o SEU tamanho de mapa (a 1 é compacta, a 3 é a maior). */
  half: number;
  groundMat: THREE.MeshStandardMaterial;
  /** 10.9GQ — clones das texturas do chão (repeat próprio da região); descartados na troca. */
  groundTex: THREE.Texture[];
  obstacles: Obstacle[];
  buckets: Map<number, Obstacle[]>;
  spores: SporeBatch[];
  crystalMats: THREE.MeshStandardMaterial[];
  waterMat: THREE.MeshStandardMaterial | null;
  cloudTex: THREE.CanvasTexture | null;
  flocks: Flock[];
  /** 10.9BR — o balanço da grama vive no VERTEX SHADER; o update só sobe este uniform. */
  grassTime: { value: number } | null;
  /** 10.6K — materiais da muralha (recoloridos pelo tema da região). */
  wallMats: THREE.MeshStandardMaterial[];
  crestMat: THREE.MeshBasicMaterial | null;
  /** 10.9BR — chunks de decoração (culling por distância a cada 0,5 s). */
  chunks: Chunk[];
  /** 10.9BR — materiais EXCLUSIVOS desta região (clones/criados aqui): são descartados na troca.
   *  Os da biblioteca (Materials.ts) NUNCA entram aqui. */
  ownedMats: THREE.Material[];
  /** 10.9BS — lagos colocados pelo buildProps (os juncos do pântano nascem em volta deles). */
  ponds: { x: number; z: number; r: number }[];
  /** 10.9BS — lotes de vagalumes (a opacidade sobe à noite). */
  fireflies: SporeBatch[];
}

const BUCKET = 14; // metros por bucket de colisão
/** 10.9BR — célula do culling de decoração e raio a partir do qual o chunk é desligado.
 *  Com a névoa exp2 0.0085, a 150 m só ~14 % do contraste sobrevive: o pop é imperceptível. */
const CHUNK = 60;
// 10.9HY — era const 150. Agora vem do preset (low corta a 85 m): é o que decide quantos
// InstancedMesh de cenário entram no render E na passada de sombra.
const cullDist = (): number => quality.values.cullDist;
const _m = new THREE.Object3D();
const _c = new THREE.Color();

/**
 * 10.9BR — LOTE INSTANCIADO POR CHUNK. Acumula (matriz, cor) e, no `build`, cria UM InstancedMesh
 * por célula de 60×60 m — todos compartilhando geometria e material. Cada chunk tem
 * `frustumCulled = true` e boundingSphere própria, então o culling nativo já corta o que está fora
 * da tela; o Arena.update ainda desliga os chunks longe do player. Antes: um único InstancedMesh
 * por tipo cobria o mapa inteiro (bounding sphere = mapa todo → nunca era cortado e desenhava
 * TODAS as instâncias, inclusive as a 400 m).
 */
class InstanceBatch {
  private readonly items: { m: THREE.Matrix4; c: THREE.Color; ix: number; iz: number }[] = [];
  constructor(
    private readonly geo: THREE.BufferGeometry,
    private readonly mat: THREE.Material,
    private readonly opts: { castShadow?: boolean; receiveShadow?: boolean } = {},
  ) {}
  /** Registra uma instância a partir do Object3D auxiliar já com `updateMatrix()` feito. */
  add(obj: THREE.Object3D, color: THREE.Color): void {
    const e = obj.matrix.elements;
    this.items.push({ m: obj.matrix.clone(), c: color.clone(), ix: Math.floor(e[12] / CHUNK), iz: Math.floor(e[14] / CHUNK) });
  }
  get size(): number {
    return this.items.length;
  }
  build(parent: THREE.Group, chunks: Chunk[]): void {
    const byCell = new Map<number, typeof this.items>();
    for (const it of this.items) {
      const key = (it.iz + 512) * 2048 + (it.ix + 512);
      let list = byCell.get(key);
      if (!list) { list = []; byCell.set(key, list); }
      list.push(it);
    }
    for (const list of byCell.values()) {
      const im = new THREE.InstancedMesh(this.geo, this.mat, list.length);
      for (let i = 0; i < list.length; i++) {
        im.setMatrixAt(i, list[i].m);
        im.setColorAt(i, list[i].c);
      }
      im.instanceMatrix.needsUpdate = true;
      if (im.instanceColor) im.instanceColor.needsUpdate = true;
      // 10.9HY/HZ — no preset baixo/médio o CENÁRIO não projeta sombra (árvore/pedra/muro eram
      // redesenhados inteiros na passada de sombra). Marcado com `decorCaster` pra o
      // `Renderer.applyQuality` reaplicar a flag quando o preset muda em runtime.
      if (this.opts.castShadow) im.userData.decorCaster = true;
      im.castShadow = !!this.opts.castShadow && quality.values.decorShadows;
      im.receiveShadow = !!this.opts.receiveShadow;
      im.frustumCulled = true;
      im.computeBoundingSphere();
      parent.add(im);
      chunks.push({ im, cx: (list[0].ix + 0.5) * CHUNK, cz: (list[0].iz + 0.5) * CHUNK });
    }
    this.items.length = 0;
  }
}
/** 10.5R — tom de contraste da vegetação rasteira (senão some na cor do bioma). */
const GRASS_TINT = new THREE.Color(0xb6dd72);
/** 10.6B — terra pisada nas clareiras grandes (quebra a cor chapada do bioma). */
const EARTH_TINT = new THREE.Color(0x9d8b63);
/** 10.6B — baixadas úmidas: mais escuras e frias que o resto do bioma. */
const DAMP_TINT = new THREE.Color(0x2b3a44);

/**
 * 10.6B — PISO DE LUMINÂNCIA. Antes o chão era um plano sem luz por cima, então cores quase
 * pretas (folhagem 0x16301f, chão 0x2f5a2a…) ainda apareciam. Agora que tudo é iluminado de
 * verdade e o preenchimento caiu, essas cores viram manchas pretas. Esta função levanta a
 * cor até um mínimo de luminância SEM mexer no matiz — a paleta continua a mesma, só sai do
 * breu. Trabalha em espaço linear (é assim que THREE.Color guarda).
 */
function lift(c: THREE.Color, minL: number): THREE.Color {
  const l = c.r * 0.3 + c.g * 0.59 + c.b * 0.11;
  if (l > 0.0001 && l < minL) c.multiplyScalar(minL / l);
  return c;
}

/** 10.6B — recorte de um tufo de mato (alphaTest). Feito UMA vez e compartilhado. */
let _tuftTex: THREE.CanvasTexture | null = null;
function makeTuftTexture(): THREE.CanvasTexture {
  if (_tuftTex) return _tuftTex;
  const S = 64;
  const c = document.createElement("canvas");
  c.width = c.height = S;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, S, S);
  // 7 folhas saindo da base, afinando para a ponta
  for (let i = 0; i < 7; i++) {
    const baseX = 10 + (i / 6) * 44 + (Math.random() - 0.5) * 5;
    const tipX = baseX + (baseX - S / 2) * 0.55 + (Math.random() - 0.5) * 9;
    const tipY = 6 + Math.random() * 22;
    const w = 2.6 + Math.random() * 2.4;
    const g = 200 + Math.floor(Math.random() * 55);
    ctx.fillStyle = `rgb(${g},${g},${g})`;
    ctx.beginPath();
    ctx.moveTo(baseX - w, S);
    ctx.quadraticCurveTo(baseX - w * 0.6, S * 0.5, tipX, tipY);
    ctx.quadraticCurveTo(baseX + w * 0.6, S * 0.5, baseX + w, S);
    ctx.closePath();
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 4;
  _tuftTex = tex;
  return tex;
}

/** 10.6B — ruído de valor 2D determinístico (0..1), suave. Usado para pintar o chão:
 *  deforma as fronteiras de bioma e cria variação de tom em várias escalas. Barato e
 *  roda só uma vez, na construção da região. */
function hash2(ix: number, iz: number): number {
  const s = Math.sin(ix * 127.1 + iz * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
function noise2(x: number, z: number): number {
  const x0 = Math.floor(x), z0 = Math.floor(z);
  const fx = x - x0, fz = z - z0;
  const ux = fx * fx * (3 - 2 * fx);
  const uz = fz * fz * (3 - 2 * fz);
  const a = hash2(x0, z0), b = hash2(x0 + 1, z0), c = hash2(x0, z0 + 1), d = hash2(x0 + 1, z0 + 1);
  return a * (1 - ux) * (1 - uz) + b * ux * (1 - uz) + c * (1 - ux) * uz + d * ux * uz;
}

export class Arena {
  readonly group = new THREE.Group();
  /** Tamanho (meia-largura) da região ATIVA. 10.6H: deixou de ser um número global. */
  get halfSize(): number {
    return this.active.half;
  }

  private active: RegionBuild;
  private activeId = "forgottenForest";
  private tAcc = 0;
  /** 10.9BR — acumuladores: culling de chunks (0,5 s) e esporos (30 Hz). */
  private cullAcc = 1;
  private sporeAcc = 0;
  /** 10.9BS — fator dia (0 = noite, 1 = meio-dia), informado pelo Game: vagalumes acendem à noite. */
  dayLight = 1;

  constructor(scene: THREE.Scene) {
    this.active = this.buildRegion("forgottenForest");
    scene.add(this.group);
  }

  /** Obstáculos da região ativa. */
  get obstacles(): Obstacle[] {
    return this.active.obstacles;
  }

  /**
   * 10.5S — troca o chão para a região informada.
   * 10.9BR — SÓ A REGIÃO ATUAL FICA VIVA: antes todas as já visitadas ficavam em memória
   * (geometrias + texturas + InstancedMesh de milhares de instâncias, invisíveis). Agora a
   * anterior é descartada (dispose de geometria/textura/materiais exclusivos) e a nova é
   * reconstruída — o build é procedural e leva poucas dezenas de ms.
   */
  setRegion(id: string): void {
    if (id === this.activeId) return;
    this.disposeBuild(this.active);
    this.active = this.buildRegion(id);
    this.activeId = id;
    this.cullAcc = 1; // aplica o culling de chunks já no próximo frame
  }

  /** 10.9BR — libera tudo que é exclusivo de uma região. Materiais da biblioteca ficam intactos. */
  private disposeBuild(b: RegionBuild): void {
    this.group.remove(b.group);
    const geos = new Set<THREE.BufferGeometry>();
    b.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) geos.add(mesh.geometry);
      if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
    });
    for (const g of geos) g.dispose();
    for (const t of b.groundTex) t.dispose(); // 10.9GQ (só os clones; o cache global fica)
    b.groundTex.length = 0;
    b.groundMat.dispose();
    b.cloudTex?.dispose();
    for (const m of b.ownedMats) m.dispose();
    b.ownedMats.length = 0;
    b.chunks.length = 0;
    b.spores.length = 0;
    b.fireflies.length = 0;
    b.ponds.length = 0;
    b.flocks.length = 0;
    b.crystalMats.length = 0;
    b.obstacles.length = 0;
    b.buckets.clear();
  }

  /** Retema a cor do chão (RegionManager). 10.6B: o bioma agora está pintado nos VÉRTICES,
   *  então a cor da região entra só como um véu leve por cima (senão apagaria os biomas). */
  setGroundColor(color: number): void {
    this.active.groundMat.color.setRGB(1, 1, 1).lerp(_c.setHex(color), 0.14);
  }

  /** 10.6K — pinta a muralha com o sotaque da região (a crista usa o acento do tema). */
  setWallTheme(rock: number, accent: number): void {
    const b = this.active;
    // a pedra da região entra escurecida: no tom cheio a muralha ficava clara demais e
    // parecia papelão em vez de penhasco
    if (b.wallMats[0]) b.wallMats[0].color.setHex(rock).multiplyScalar(0.5);
    if (b.wallMats[1]) b.wallMats[1].color.setHex(rock).multiplyScalar(0.34);
    b.crestMat?.color.setHex(accent);
  }

  // =============================================================== build
  private buildRegion(regionId: string): RegionBuild {
    const b: RegionBuild = {
      group: new THREE.Group(),
      half: regionHalfSize(regionId),
      groundMat: new THREE.MeshStandardMaterial({ color: 0x2c3d1c, roughness: 1, metalness: 0 }),
      groundTex: [],
      obstacles: [],
      buckets: new Map(),
      spores: [],
      crystalMats: [],
      waterMat: null,
      cloudTex: null,
      flocks: [],
      grassTime: null,
      wallMats: [],
      crestMat: null,
      chunks: [],
      ownedMats: [],
      ponds: [],
      fireflies: [],
    };
    const terrs = territoriesForRegion(regionId);
    const clearings = this.clearingsFor(regionId);
    this.buildGround(b, terrs, regionId);
    this.buildDetail(b, terrs, clearings);
    this.buildDecals(b, terrs, clearings); // 10.9GQ: rachaduras, musgo e runas no chão
    this.buildDecor(b, terrs, clearings);
    this.buildProps(b, terrs, clearings);
    this.buildBiomeProps(b, terrs, clearings); // 10.9BS: identidade por bioma
    this.buildAmbient(b, terrs, regionId);
    this.buildBoundary(b);
    this.group.add(b.group);
    return b;
  }

  /** Áreas que ficam LIMPAS de decoração (base, arena de boss, portais). */
  private clearingsFor(regionId: string): { x: number; z: number; r: number }[] {
    const out = [{ x: 0, z: 0, r: GameConfig.sanctuary.radius + 4 }];
    const rc = GameConfig.world.regions.find((r) => r.id === regionId)?.rift;
    if (rc) out.push({ x: rc.x, z: rc.z, r: 5 });
    if (regionId === "forgottenForest") {
      const a = GameConfig.boss.voidAlpha.arena;
      out.push({ x: a.center.x, z: a.center.z, r: a.radius + 2 });
      out.push({ x: GameConfig.altar.position.x, z: GameConfig.altar.position.z, r: 4 });
    }
    if (regionId === "shatteredWilds") {
      const a = GameConfig.boss.aetherColossus.arena;
      out.push({ x: a.center.x, z: a.center.z, r: a.radius + 2 });
    }
    return out;
  }

  // ---------------------------------------------------------------- chão
  /**
   * 10.6B — CHÃO PINTADO NOS VÉRTICES. Antes: um plano liso de UMA cor + 3 "manchas" de
   * bioma por território (planos MeshBasicMaterial, sem luz, por cima). Resultado: campo
   * roxo chapado, plástico, SEM sombra nenhuma — porque as manchas não recebiam luz e ainda
   * cobriam as sombras de contato e a sombra do sol.
   *
   * Agora o chão é UM único plano subdividido (96×96) e a cor do bioma é pintada VÉRTICE A
   * VÉRTICE, com as fronteiras deformadas por ruído. Ganhos:
   *   · transições de bioma orgânicas em vez de manchas redondas borradas;
   *   · o chão volta a RECEBER sombra (do sol e dos blobs de contato);
   *   · variação de tom em 3 escalas de ruído — acaba a sensação de cor chapada;
   *   · uma mancha de sujeira por bioma seguindo a textura;
   *   · menos um draw call (as manchas sumiram).
   */
  private buildGround(b: RegionBuild, terrs: Territory[], regionId: string): void {
    const gs = REGION_GROUND[regionId] ?? REGION_GROUND.forgottenForest;
    const size = b.half * 2 + 12;
    const SEG = 96;
    const geo = new THREE.PlaneGeometry(size, size, SEG, SEG);
    const pos = geo.attributes.position;
    const col = new Float32Array(pos.count * 3);
    const spl = new Float32Array(pos.count); // 10.9GQ — peso da textura B (splat)
    const base = new THREE.Color();
    const mix = new THREE.Color();

    for (let v = 0; v < pos.count; v++) {
      const x = pos.getX(v);
      const z = -pos.getY(v); // rotation.x = -PI/2 leva (px,py,0) para (px,0,-py)
      base.setRGB(0.42, 0.46, 0.3); // fallback (nunca aparece: os territórios cobrem a grade)
      if (terrs.length > 0) {
        // fronteiras deformadas: desloca a amostra com ruído antes de medir a distância
        const wx = x + (noise2(x * 0.045, z * 0.045) - 0.5) * 13 + (noise2(x * 0.13, z * 0.13) - 0.5) * 4.5;
        const wz = z + (noise2(x * 0.045 + 41, z * 0.045 - 17) - 0.5) * 13 + (noise2(x * 0.13 - 9, z * 0.13 + 5) - 0.5) * 4.5;
        let sum = 0, r = 0, g = 0, bl = 0, w1 = 0, w2 = 0, nearD = 1e9, nearHex = terrs[0].biome.ground, sp = 0;
        for (const t of terrs) {
          const dx = Math.abs(wx - t.cx) / t.halfW;
          const dz = Math.abs(wz - t.cz) / t.halfH;
          const d = Math.max(dx, dz);
          if (d < nearD) { nearD = d; nearHex = t.biome.ground; }
          const k = 1.42 - d;
          if (k <= 0) continue;
          const w = k * k * k;
          if (w > w1) { w2 = w1; w1 = w; } else if (w > w2) { w2 = w; }
          lift(mix.setHex(t.biome.ground), 0.17);
          r += mix.r * w; g += mix.g * w; bl += mix.b * w; sum += w;
          sp += (gs.splat[t.def.biome] ?? 0) * w;
        }
        if (sum > 0) base.setRGB(r / sum, g / sum, bl / sum);
        else lift(base.setHex(nearHex), 0.17);
        spl[v] = sum > 0 ? sp / sum : 0;
        // costura entre biomas: escurece de leve onde dois pesos empatam (lê como divisa)
        if (w1 > 0 && w2 / w1 > 0.72) base.multiplyScalar(0.9);
      }
      // variação de tom em 3 escalas (grandes clareiras, manchas médias, granulado)
      const nBig = noise2(x * 0.017 + 3.1, z * 0.017 - 7.4);
      const nMid = noise2(x * 0.062 - 12, z * 0.062 + 22);
      const nFine = noise2(x * 0.19 + 61, z * 0.19 + 8);
      const shade = 0.66 + nBig * 0.42 + nMid * 0.26 + nFine * 0.12;
      base.multiplyScalar(shade);
      // clareiras grandes puxam para um tom quente (terra pisada) — quebra a cor única
      if (nBig > 0.6) base.lerp(EARTH_TINT, (nBig - 0.6) * 1.15);
      // depressões úmidas: mais escuras e frias
      if (nBig < 0.3) base.lerp(DAMP_TINT, (0.3 - nBig) * 0.9);
      col[v * 3] = base.r;
      col[v * 3 + 1] = base.g;
      col[v * 3 + 2] = base.b;
    }
    geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
    geo.setAttribute("splat", new THREE.BufferAttribute(spl, 1));

    this.applyGroundMaterial(b, gs);
    const ground = new THREE.Mesh(geo, b.groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    b.group.add(ground);
  }

  /**
   * 10.9GQ — MATERIAL DO CHÃO: dois conjuntos (albedo + normal map) misturados pelo atributo
   * `splat` dos vértices. A mistura entra por onBeforeCompile (trocando só as leituras de textura
   * do MeshStandardMaterial), então sombras, névoa, cor de vértice e tone mapping seguem iguais.
   */
  private applyGroundMaterial(b: RegionBuild, gs: { a: GroundStyle; b: GroundStyle }): void {
    const A = groundTextures(gs.a), B = groundTextures(gs.b);
    const tiles = Math.max(5, Math.floor(b.half / 6.5)); // ~13 m por repetição (placas de rocha ~2 m, tufos ~1 m)
    const m = b.groundMat;
    const mapA = A.map.clone(), mapB = B.map.clone();
    mapA.repeat.set(tiles, tiles); mapB.repeat.set(tiles, tiles);
    m.map = mapA;
    m.vertexColors = true;
    m.color.setRGB(1, 1, 1);
    m.roughness = 0.94;
    const useNormal = quality.values.normalMaps;
    let nA: THREE.Texture | null = null, nB: THREE.Texture | null = null;
    if (useNormal) {
      nA = A.normalMap.clone(); nB = B.normalMap.clone();
      nA.repeat.set(tiles, tiles); nB.repeat.set(tiles, tiles);
      m.normalMap = nA;
      m.normalScale.set(0.75, 0.75);
    }
    b.groundTex = [mapA, mapB, nA, nB].filter((t): t is THREE.Texture => !!t);
    m.onBeforeCompile = (shader) => {
      shader.uniforms.map2 = { value: mapB };
      shader.uniforms.normalMap2 = { value: nB };
      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", "#include <common>\nattribute float splat;\nvarying float vSplat;")
        .replace("#include <begin_vertex>", "#include <begin_vertex>\nvSplat = splat;");
      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", "#include <common>\nuniform sampler2D map2;\nuniform sampler2D normalMap2;\nvarying float vSplat;")
        // os #include só são expandidos DEPOIS do onBeforeCompile: troca o include pelo chunk já editado
        .replace("#include <map_fragment>", THREE.ShaderChunk.map_fragment.replace("texture2D( map, vMapUv )", "mix( texture2D( map, vMapUv ), texture2D( map2, vMapUv ), vSplat )"))
        .replace("#include <normal_fragment_maps>", THREE.ShaderChunk.normal_fragment_maps.replace("texture2D( normalMap, vNormalMapUv )", "mix( texture2D( normalMap, vNormalMapUv ), texture2D( normalMap2, vNormalMapUv ), vSplat )"));
    };
    m.customProgramCacheKey = () => `vhGround${useNormal ? "N" : ""}`;
    m.needsUpdate = true;
  }

  /**
   * 10.9GQ — DECALS DO CHÃO: rachaduras (biomas pedregosos), manchas de musgo (biomas verdes) e
   * runas do Void (biomas de cristal/ruína). Planos com recorte alfa, instanciados por chunk
   * (3 draw calls por célula visível), sem colisão. Dão "história" ao chão sem geometria nova.
   */
  private buildDecals(b: RegionBuild, terrs: Territory[], clearings: { x: number; z: number; r: number }[]): void {
    if (terrs.length === 0) return;
    const ds = quality.values.particleScale;
    const mk = (kind: "crack" | "moss" | "rune", glow = 0): THREE.MeshStandardMaterial => {
      const m = new THREE.MeshStandardMaterial({
        map: decalTexture(kind), alphaTest: 0.35, roughness: 1, metalness: 0, color: 0xffffff,
        depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
      });
      if (glow > 0) { m.emissive.setHex(0x8e6cff); m.emissiveMap = decalTexture(kind); m.emissiveIntensity = glow; }
      b.ownedMats.push(m);
      return m;
    };
    const plane = new THREE.PlaneGeometry(1, 1);
    const cracks = new InstanceBatch(plane, mk("crack"), { receiveShadow: true });
    const moss = new InstanceBatch(plane, mk("moss"), { receiveShadow: true });
    const runes = new InstanceBatch(plane, mk("rune", 0.55), { receiveShadow: true });
    const col = new THREE.Color();
    for (const t of terrs) {
      const bio = t.biome;
      const rocky = bio.rocks >= 14 || bio.grass <= 10;
      const mystic = bio.crystals >= 7 || t.def.biome === "ruin" || t.def.biome === "den";
      const nC = Math.round((rocky ? 13 : 4) * ds);
      const nM = Math.round((rocky ? 3 : 11) * ds);
      const nR = Math.round((mystic ? 5 : 1) * ds);
      const put = (batch: InstanceBatch, size: number, y: number, c: THREE.Color): void => {
        const p = this.scatterIn(t, clearings);
        if (!p) return;
        _m.position.set(p.x, y, p.z);
        _m.rotation.set(-Math.PI / 2, 0, randRange(0, Math.PI * 2));
        _m.scale.set(size, size * randRange(0.8, 1.2), 1);
        _m.updateMatrix();
        batch.add(_m, c);
      };
      for (let k = 0; k < nC; k++) put(cracks, randRange(2.6, 5.2), 0.03, col.setHex(bio.rock).multiplyScalar(0.28));
      for (let k = 0; k < nM; k++) put(moss, randRange(1.6, 3.2), 0.025, lift(col.setHex(bio.foliage[k % bio.foliage.length]).lerp(GRASS_TINT, 0.35), 0.16).multiplyScalar(randRange(0.85, 1.1)));
      for (let k = 0; k < nR; k++) put(runes, randRange(1.4, 2.3), 0.035, col.setHex(bio.crystal).lerp(_c.setHex(0xffffff), 0.25));
    }
    cracks.build(b.group, b.chunks);
    moss.build(b.group, b.chunks);
    runes.build(b.group, b.chunks);
  }

  /**
   * 10.6B — CAMADA DE DETALHE. Um território tem 70×42 m e só ~15 props: dava um campo
   * vazio, sem escala e sem nada perto do chão. Aqui entram DUAS camadas baratíssimas que
   * cobrem o terreno inteiro:
   *   · tufos: plano de 2 triângulos com recorte alfa (alphaTest, sem blending = sem
   *     ordenação), 2 cruzados por tufo, cor puxada da folhagem do bioma;
   *   · pedriscos: tetraedro de 4 triângulos, cor da rocha do bioma.
   * Tudo em 2 InstancedMesh (2 draw calls no total), sem colisão — dá para andar por cima.
   */
  private buildDetail(b: RegionBuild, terrs: Territory[], clearings: { x: number; z: number; r: number }[]): void {
    if (terrs.length === 0) return;
    // densidade acompanha o preset de gráficos (low corta pela metade — celular fraco)
    const ds = quality.values.particleScale;
    const TUFT = Math.round(150 * ds); // por território
    const PEB = Math.round(70 * ds);
    const tuftMat = new THREE.MeshLambertMaterial({
      map: makeTuftTexture(), alphaTest: 0.52, side: THREE.DoubleSide, color: 0xffffff,
    });
    b.ownedMats.push(tuftMat); // 10.9BR: material da região (a textura é global e fica)
    // 10.9BR — lotes por chunk (60×60 m): o que está longe/fora da tela não é desenhado
    const tufts = new InstanceBatch(new THREE.PlaneGeometry(1, 1), tuftMat, { receiveShadow: true });
    const pebs = new InstanceBatch(new THREE.TetrahedronGeometry(0.3, 0), stoneMat(0xffffff)); // 10.9BQ

    const fol = new THREE.Color();
    for (const t of terrs) {
      const bio = t.biome;
      // biomas pedregosos trocam metade dos tufos por pedrisco
      const stony = bio.grass <= 12;
      const nT = stony ? Math.round(TUFT * 0.45) : TUFT;
      const nP = stony ? Math.round(PEB * 1.8) : PEB;
      for (let k = 0; k < nT; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const w = randRange(0.55, 1.15);
        const h = w * randRange(0.7, 1.25);
        const rot = randRange(0, Math.PI);
        lift(fol.setHex(bio.foliage[k % bio.foliage.length]).lerp(GRASS_TINT, randRange(0.25, 0.6)), 0.14).multiplyScalar(randRange(0.8, 1.15));
        for (const extra of [0, Math.PI / 2]) {
          _m.position.set(p.x, h * 0.5, p.z);
          _m.rotation.set(0, rot + extra, 0);
          _m.scale.set(w, h, 1);
          _m.updateMatrix();
          tufts.add(_m, fol);
        }
      }
      for (let k = 0; k < nP; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const s = randRange(0.35, 1.05);
        _m.position.set(p.x, s * 0.16, p.z);
        _m.rotation.set(randRange(0, 3), randRange(0, 3), randRange(0, 3));
        _m.scale.set(s, s * randRange(0.5, 0.85), s);
        _m.updateMatrix();
        pebs.add(_m, lift(_c.setHex(bio.rock), 0.15).multiplyScalar(randRange(0.75, 1.2)));
      }
    }
    tufts.build(b.group, b.chunks);
    pebs.build(b.group, b.chunks);
  }

  // ------------------------------------------------------------ decoração
  private buildDecor(b: RegionBuild, terrs: Territory[], clearings: { x: number; z: number; r: number }[]): void {
    if (terrs.length === 0) return;

    // 10.9BQ — biblioteca: brancos (a cor vem do instanceColor do bioma). Só o cristal é clone (pulsa).
    const trunkMat = stoneMat(0xffffff);
    const foliMat = foliageMat();
    const rockMat = stoneMat(0xffffff);
    const grassMat = this.makeGrassMaterial(b); // 10.9BR: clone com vento no vertex shader
    const crystalMat = crystalStdMat(0xffffff, 0x5a3bd0, 0.7).clone();
    b.crystalMats.push(crystalMat);
    b.ownedMats.push(crystalMat);

    // 10.9BR — lotes por chunk. 10.9BQ: sombra só em árvores e pedras (cristais e grama NÃO projetam)
    const trunk = new InstanceBatch(new THREE.CylinderGeometry(0.25, 0.38, 3, 6), trunkMat, { castShadow: true });
    const foliA = new InstanceBatch(new THREE.ConeGeometry(1.6, 3.6, 7), foliMat, { castShadow: true });
    const foliB = new InstanceBatch(new THREE.ConeGeometry(1.3, 2.8, 7), foliMat, { castShadow: true });
    const foliC = new InstanceBatch(new THREE.ConeGeometry(0.95, 2.1, 6), foliMat, { castShadow: true });
    const rocks = new InstanceBatch(new THREE.IcosahedronGeometry(0.8, 0), rockMat, { castShadow: true });
    const grass = new InstanceBatch(new THREE.ConeGeometry(0.5, 1.15, 5), grassMat, { receiveShadow: true });
    const crystals = new InstanceBatch(new THREE.OctahedronGeometry(0.34, 0), crystalMat);

    for (const t of terrs) {
      const bio = t.biome;
      for (let k = 0; k < bio.trees; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const s = randRange(0.8, 1.5);
        const rot = randRange(0, Math.PI * 2);
        const fc = lift(new THREE.Color(bio.foliage[k % bio.foliage.length]), 0.1);
        this.place(trunk, p.x, 1.5 * s, p.z, s, rot, lift(_c.setHex(bio.trunk), 0.08));
        this.place(foliA, p.x, 3.15 * s, p.z, s, rot, fc);
        this.place(foliB, p.x, 4.35 * s, p.z, s, rot, fc.clone().multiplyScalar(1.08));
        this.place(foliC, p.x, 5.35 * s, p.z, s, rot, fc.clone().multiplyScalar(1.2));
        this.addObstacle(b, p.x, p.z, 0.55 * s);
      }
      for (let k = 0; k < bio.rocks; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const s = randRange(0.5, 1.7);
        _m.position.set(p.x, s * 0.3, p.z);
        _m.rotation.set(randRange(0, 3), randRange(0, 3), randRange(0, 3));
        _m.scale.set(s, s * randRange(0.6, 1), s);
        _m.updateMatrix();
        rocks.add(_m, lift(_c.setHex(bio.rock), 0.17));
        this.addObstacle(b, p.x, p.z, 0.75 * s);
      }
      for (let k = 0; k < bio.grass; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const s = randRange(0.75, 1.5);
        const sy = s * randRange(0.8, 1.5);
        _m.position.set(p.x, 0.42 * s, p.z);
        _m.rotation.set(0, randRange(0, Math.PI * 2), 0);
        _m.scale.set(s, sy, s);
        _m.updateMatrix();
        grass.add(_m, lift(_c.setHex(bio.foliage[0]).lerp(GRASS_TINT, 0.5), 0.14));
      }
      for (let k = 0; k < bio.crystals; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        for (const [ox, oz, s] of [[0, 0, 1], [0.31, 0.14, 0.62], [-0.28, 0.2, 0.5]] as const) {
          _m.position.set(p.x + ox, s * 0.52, p.z + oz);
          _m.rotation.set(0, randRange(0, Math.PI), 0);
          _m.scale.set(s, s * 1.9, s);
          _m.updateMatrix();
          crystals.add(_m, _c.setHex(bio.crystal));
        }
      }
    }
    for (const batch of [trunk, foliA, foliB, foliC, rocks, grass, crystals]) batch.build(b.group, b.chunks);
  }

  /**
   * 10.9BR — VENTO NA GRAMA NO VERTEX SHADER. Antes o update recomputava a matriz de CADA folha
   * (rotação × escala × translação) na CPU todo frame e subia o instanceMatrix inteiro pra GPU.
   * Agora o cone é deslocado no shader: o topo (position.y alto) se desloca por um seno que
   * depende do tempo e da posição da instância (instanceMatrix[3].xz), com fase por folha
   * derivada de um hash da posição. A CPU só sobe UM uniform (uTime). O material é um CLONE do
   * stoneMat branco (o original é compartilhado por pedras/troncos — não pode ganhar shader).
   * O deslocamento reproduz o antigo: ângulo `sway` aplicado à altura desde a base (1,15 · k).
   */
  private makeGrassMaterial(b: RegionBuild): THREE.MeshStandardMaterial {
    const mat = stoneMat(0xffffff).clone();
    const uTime = { value: 0 };
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = uTime;
      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", "#include <common>\nuniform float uTime;")
        .replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>
#ifdef USE_INSTANCING
{
  vec2 ip = vec2(instanceMatrix[3].x, instanceMatrix[3].z);
  float ph = fract(sin(dot(ip, vec2(12.9898, 78.233))) * 43758.5453) * 6.2832;
  float gust = 0.09 + sin(uTime * 0.35) * 0.05;
  float sway = sin(uTime * 1.9 + ph + ip.x * 0.08 + ip.y * 0.05) * gust;
  float k = clamp((position.y + 0.575) / 1.15, 0.0, 1.0) * 1.15;
  transformed.x += sway * k;
  transformed.z += sway * 0.8 * k;
}
#endif`,
        );
    };
    // chave própria: senão o three reaproveita o programa do stoneMat comum (sem o vento)
    mat.customProgramCacheKey = () => "vh-grass-sway-10.9BR";
    b.grassTime = uTime;
    b.ownedMats.push(mat);
    return mat;
  }

  private place(batch: InstanceBatch, x: number, y: number, z: number, s: number, rotY: number, color: THREE.Color): void {
    _m.position.set(x, y, z);
    _m.rotation.set(0, rotY, 0);
    _m.scale.setScalar(s);
    _m.updateMatrix();
    batch.add(_m, color);
  }

  private scatterIn(t: Territory, clearings: { x: number; z: number; r: number }[]): { x: number; z: number } | null {
    for (let i = 0; i < 8; i++) {
      const x = t.cx + randRange(-t.halfW + 2, t.halfW - 2);
      const z = t.cz + randRange(-t.halfH + 2, t.halfH - 2);
      let ok = true;
      for (const c of clearings) {
        if (Math.hypot(x - c.x, z - c.z) < c.r) { ok = false; break; }
      }
      if (ok) return { x, z };
    }
    return null;
  }

  /** PROPS por bioma: lagos, arbustos, flores luminosas, troncos caídos e árvores secas. */
  private buildProps(b: RegionBuild, terrs: Territory[], clearings: { x: number; z: number; r: number }[]): void {
    if (terrs.length === 0) return;
    const counts = (t: Territory): { bush: number; flower: number; log: number; dead: number; pond: number } => {
      const bio = t.def.biome;
      const wet = bio === "marsh" || bio === "fissure";
      const woody = bio === "grove" || bio === "thicket" || bio === "wind";
      return {
        bush: Math.round(t.biome.grass * 0.4),
        flower: bio === "rocky" || bio === "crag" ? 3 : Math.round(t.biome.grass * 0.55),
        log: woody ? 3 : 1,
        dead: bio === "den" || bio === "rocky" || bio === "storm" ? 4 : 0,
        pond: wet ? 2 : woody ? 1 : bio === "frost" ? 2 : 0, // 10.9BS: frost ganha lagos (congelados)
      };
    };
    // 10.9BQ — biblioteca (brancos + instanceColor); a flor é clone (pulsa com os cristais)
    const bushMat = foliageMat();
    const flowerMat = crystalStdMat(0xffffff, 0x333333, 1.1).clone();
    const woodMat = stoneMat(0xffffff);
    const waterMat = new THREE.MeshStandardMaterial({ color: 0x2f6f8a, roughness: 0.15, metalness: 0.35, transparent: true, opacity: 0.82 });
    b.waterMat = waterMat;
    b.ownedMats.push(flowerMat, waterMat); // 10.9BR

    // 10.9BR — lotes por chunk
    const bush = new InstanceBatch(new THREE.IcosahedronGeometry(0.62, 0), bushMat, { castShadow: true });
    const flower = new InstanceBatch(new THREE.OctahedronGeometry(0.13, 0), flowerMat);
    const wood = new InstanceBatch(new THREE.CylinderGeometry(0.22, 0.26, 2.2, 6), woodMat, { castShadow: true });
    const pond = new InstanceBatch(new THREE.CircleGeometry(1, 22), waterMat);

    for (const t of terrs) {
      const c = counts(t);
      const bio = t.biome;
      for (let k = 0; k < c.bush; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const s = randRange(0.8, 1.5);
        _m.position.set(p.x, s * 0.42, p.z);
        _m.rotation.set(randRange(0, 1), randRange(0, 6), randRange(0, 1));
        _m.scale.set(s, s * 0.75, s);
        _m.updateMatrix();
        bush.add(_m, lift(_c.setHex(bio.foliage[k % bio.foliage.length]).lerp(GRASS_TINT, 0.22), 0.12));
      }
      for (let k = 0; k < c.flower; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const s = randRange(0.7, 1.4);
        _m.position.set(p.x, 0.16 * s, p.z);
        _m.rotation.set(0, randRange(0, 6), 0);
        _m.scale.setScalar(s);
        _m.updateMatrix();
        flower.add(_m, _c.setHex(bio.crystal));
      }
      for (let k = 0; k < c.log; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const s = randRange(0.8, 1.35);
        _m.position.set(p.x, 0.26 * s, p.z);
        _m.rotation.set(Math.PI / 2, randRange(0, Math.PI), randRange(-0.1, 0.1));
        _m.scale.set(s, s * randRange(0.9, 1.6), s);
        _m.updateMatrix();
        wood.add(_m, lift(_c.setHex(bio.trunk), 0.1));
        this.addObstacle(b, p.x, p.z, 0.5 * s);
      }
      for (let k = 0; k < c.dead; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const s = randRange(0.9, 1.5);
        _m.position.set(p.x, 1.5 * s, p.z);
        _m.rotation.set(randRange(-0.06, 0.06), randRange(0, 6), randRange(-0.06, 0.06));
        _m.scale.set(s * 0.7, s, s * 0.7);
        _m.updateMatrix();
        wood.add(_m, lift(_c.setHex(bio.trunk), 0.11).multiplyScalar(0.85));
        this.addObstacle(b, p.x, p.z, 0.5 * s);
        for (let j = 0; j < 3; j++) {
          const a = (j / 3) * Math.PI * 2 + randRange(0, 1);
          _m.position.set(p.x + Math.cos(a) * 0.5 * s, (2.2 + j * 0.35) * s, p.z + Math.sin(a) * 0.5 * s);
          _m.rotation.set(Math.PI / 2.6, a, 0);
          _m.scale.set(s * 0.2, s * 0.55, s * 0.2);
          _m.updateMatrix();
          wood.add(_m, lift(_c.setHex(bio.trunk), 0.11).multiplyScalar(0.8));
        }
      }
      for (let k = 0; k < c.pond; k++) {
        const p = this.scatterIn(t, clearings);
        if (!p) continue;
        const r = randRange(3.2, 6.5);
        b.ponds.push({ x: p.x, z: p.z, r }); // 10.9BS: os juncos nascem em volta
        const frozen = t.def.biome === "frost"; // 10.9BS: lago congelado = tinta clara, quase branca
        for (const [rr, y, tint] of [[r, 0.06, 1], [r * 1.18, 0.045, 0.55]] as const) {
          _m.position.set(p.x, y, p.z);
          _m.rotation.set(-Math.PI / 2, 0, randRange(0, 6));
          _m.scale.set(rr, rr * randRange(0.75, 1), 1);
          _m.updateMatrix();
          if (frozen) pond.add(_m, _c.setHex(0xdff4ff).multiplyScalar(tint === 1 ? 1.1 : 1.35));
          else pond.add(_m, _c.setHex(bio.crystal).lerp(new THREE.Color(0x2f6f8a), 0.6).multiplyScalar(tint === 1 ? 1 : 1.5));
        }
      }
    }
    for (const batch of [bush, flower, wood, pond]) batch.build(b.group, b.chunks);
    b.crystalMats.push(flowerMat);
  }

  /** 10.9BS — ponto livre de clareira (usado pelos props que nascem perto de algo, ex.: juncos). */
  private freeAt(x: number, z: number, clearings: { x: number; z: number; r: number }[], half: number): boolean {
    if (Math.abs(x) > half - 3 || Math.abs(z) > half - 3) return false;
    for (const c of clearings) if (Math.hypot(x - c.x, z - c.z) < c.r) return false;
    return true;
  }

  /**
   * 10.9BS — PROPS DE IDENTIDADE POR BIOMA. Antes todos os 16 biomas usavam o mesmo kit
   * (árvore, pedra, arbusto, flor, tronco, lago) e só a cor mudava — de longe a Floresta e a
   * Tempestade eram o mesmo mapa. Aqui cada família de bioma ganha 2–4 props próprios, TODOS
   * em InstanceBatch por chunk (culling nativo + por distância), sem luz nova e com sombra só
   * no que é grande (colunas, pilares, rochas flutuantes). Densidade modesta por território:
   * 2–6 "grandes" e 10–20 pequenos. Respeita clareiras (base, bosses, portal).
   *
   *   grove/thicket        cogumelos (chapéu emissivo fraco), tocos, cercas de pedra baixas
   *   crystal/shard/fissure cristais grandes (2–4 m, energyMat crystal), geodos (2 metades)
   *   rocky/crag/ruin      ruínas: colunas quebradas, arcos caídos, lajes, totens de pedra
   *   marsh                juncos altos em volta dos lagos, névoa baixa (planos translúcidos)
   *   wind/galewind/storm/thunder  pilares de vento (anel Torus energizado), rochas
   *                        flutuantes com sombra blob; thunder: raios estáticos
   *   frost                estalagmites de gelo (+ lagos congelados no buildProps)
   *   ember                fendas de lava (plano emissivo no chão), rochas escuras
   *   den                  ossos/presas (2 cones), totens do Alfa
   */
  private buildBiomeProps(b: RegionBuild, terrs: Territory[], clearings: { x: number; z: number; r: number }[]): void {
    if (terrs.length === 0) return;
    const ds = quality.values.particleScale;
    const small = (n: number): number => Math.max(3, Math.round(n * ds));
    const stone = stoneMat(0xffffff);
    const capMat = crystalStdMat(0xffffff, 0xffffff, 0.3); // chapéu de cogumelo / gelo: emissivo fraco, sem pulso
    const bigCrystalMat = energyMat(0xffffff, "crystal");
    const groundGlowMat = energyMat(0xffffff, "glow"); // fendas de lava (plano virado pra cima)
    const boltMat = energyMat(0xffffff, "disc"); // raio estático (DoubleSide)
    const ringMat = energyMat(0xffffff, "ring");
    // 10.9BS — materiais exclusivos da região (névoa baixa e sombra blob): translúcidos, sem textura
    const mistMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.12, depthWrite: false, side: THREE.DoubleSide });
    const blobMat = new THREE.MeshBasicMaterial({ color: 0x0a0c14, transparent: true, opacity: 0.32, depthWrite: false });
    b.ownedMats.push(mistMat, blobMat);

    // ---- lotes (geometrias de primitivas compostas; 2–3 lotes por prop)
    const shroomStem = new InstanceBatch(new THREE.CylinderGeometry(0.07, 0.11, 0.5, 5), stone);
    const shroomCap = new InstanceBatch(new THREE.SphereGeometry(0.3, 7, 4), capMat);
    const stump = new InstanceBatch(new THREE.CylinderGeometry(0.42, 0.55, 0.6, 7), stone, { castShadow: true });
    const fence = new InstanceBatch(new THREE.BoxGeometry(0.95, 0.5, 0.36), stone);
    const bigCrystal = new InstanceBatch(new THREE.OctahedronGeometry(0.5, 0), bigCrystalMat);
    const crystalBase = new InstanceBatch(new THREE.DodecahedronGeometry(0.7, 0), stone, { castShadow: true });
    const geodeHalf = new InstanceBatch(new THREE.DodecahedronGeometry(0.9, 0), stone, { castShadow: true });
    const geodeCore = new InstanceBatch(new THREE.OctahedronGeometry(0.28, 0), bigCrystalMat);
    const column = new InstanceBatch(new THREE.CylinderGeometry(0.5, 0.62, 1, 8), stone, { castShadow: true });
    const beam = new InstanceBatch(new THREE.BoxGeometry(0.8, 0.8, 1), stone, { castShadow: true });
    const slab = new InstanceBatch(new THREE.BoxGeometry(1, 0.3, 1), stone);
    const totem = new InstanceBatch(new THREE.BoxGeometry(1, 1, 1), stone, { castShadow: true });
    const reed = new InstanceBatch(new THREE.ConeGeometry(0.07, 2.2, 4), stone);
    const mist = new InstanceBatch(new THREE.PlaneGeometry(1, 1), mistMat);
    const pillar = new InstanceBatch(new THREE.CylinderGeometry(0.32, 0.5, 7, 7), stone, { castShadow: true });
    const pillarRing = new InstanceBatch(new THREE.TorusGeometry(0.95, 0.09, 6, 20), ringMat);
    const floatRock = new InstanceBatch(new THREE.DodecahedronGeometry(1.2, 0), stone, { castShadow: true });
    const blob = new InstanceBatch(new THREE.CircleGeometry(1, 16), blobMat);
    const bolt = new InstanceBatch(new THREE.PlaneGeometry(0.5, 1), boltMat);
    const icicle = new InstanceBatch(new THREE.ConeGeometry(0.4, 2.2, 6), capMat);
    const lava = new InstanceBatch(new THREE.PlaneGeometry(1, 1), groundGlowMat);
    const darkRock = new InstanceBatch(new THREE.IcosahedronGeometry(0.9, 0), stone, { castShadow: true });
    const bone = new InstanceBatch(new THREE.ConeGeometry(0.11, 1.6, 5), stone);

    const BONE = new THREE.Color(0xd9d1c2);
    const DARK = new THREE.Color(0x2a1c18);

    for (const t of terrs) {
      const bio = t.def.biome;
      const pal = t.biome;
      const rockC = lift(new THREE.Color(pal.rock), 0.17);
      const trunkC = lift(new THREE.Color(pal.trunk), 0.1);
      const cryC = new THREE.Color(pal.crystal);

      // ================= FLORESTA: cogumelos, tocos, cercas
      if (bio === "grove" || bio === "thicket") {
        for (let k = 0; k < small(16); k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          // 2–4 cogumelos juntos
          const n = 2 + Math.floor(randRange(0, 3));
          for (let j = 0; j < n; j++) {
            const s = randRange(0.6, 1.4);
            const x = p.x + randRange(-0.6, 0.6), z = p.z + randRange(-0.6, 0.6);
            this.place(shroomStem, x, 0.25 * s, z, s, 0, _c.setHex(0xe8dcc4).multiplyScalar(randRange(0.8, 1)));
            _m.position.set(x, 0.5 * s, z);
            _m.rotation.set(0, randRange(0, 6), 0);
            _m.scale.set(s, s * 0.45, s);
            _m.updateMatrix();
            shroomCap.add(_m, _c.setHex(pal.crystal).lerp(new THREE.Color(0xffb070), 0.35).multiplyScalar(randRange(0.85, 1.1)));
          }
        }
        for (let k = 0; k < 4; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          const s = randRange(0.8, 1.4);
          this.place(stump, p.x, 0.3 * s, p.z, s, randRange(0, 6), trunkC);
          this.addObstacle(b, p.x, p.z, 0.5 * s);
        }
        if (Math.random() < 0.45) {
          const p = this.scatterIn(t, clearings);
          if (p) {
            const a = randRange(0, Math.PI);
            const n = 4 + Math.floor(randRange(0, 3));
            for (let j = 0; j < n; j++) {
              const x = p.x + Math.cos(a) * j * 1.0, z = p.z + Math.sin(a) * j * 1.0;
              if (!this.freeAt(x, z, clearings, b.half)) continue;
              _m.position.set(x, 0.22, z);
              _m.rotation.set(randRange(-0.05, 0.05), -a, randRange(-0.05, 0.05));
              _m.scale.set(1, randRange(0.8, 1.2), 1);
              _m.updateMatrix();
              fence.add(_m, rockC.clone().multiplyScalar(randRange(0.8, 1.1)));
              this.addObstacle(b, x, z, 0.5);
            }
          }
        }
      }

      // ================= CRISTAL: agrupamentos grandes e geodos
      if (bio === "crystal" || bio === "shard" || bio === "fissure") {
        for (let k = 0; k < 4; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          // base de pedra + 3–5 cristais de 2–4 m inclinados
          this.place(crystalBase, p.x, 0.25, p.z, randRange(1.2, 1.8), randRange(0, 6), rockC);
          const n = 3 + Math.floor(randRange(0, 3));
          for (let j = 0; j < n; j++) {
            const h = randRange(2, 4);
            const a = (j / n) * Math.PI * 2 + randRange(0, 0.8);
            const rr = j === 0 ? 0 : randRange(0.4, 1.0);
            _m.position.set(p.x + Math.cos(a) * rr, h * 0.42, p.z + Math.sin(a) * rr);
            _m.rotation.set(j === 0 ? 0 : randRange(0.1, 0.4) * Math.cos(a), randRange(0, 6), j === 0 ? 0 : randRange(0.1, 0.4) * Math.sin(a));
            _m.scale.set(randRange(0.5, 0.8), h, randRange(0.5, 0.8));
            _m.updateMatrix();
            bigCrystal.add(_m, cryC.clone().multiplyScalar(randRange(0.8, 1.1)));
          }
          this.addObstacle(b, p.x, p.z, 1.3);
        }
        for (let k = 0; k < 2; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          const s = randRange(1.0, 1.6);
          const a = randRange(0, Math.PI * 2);
          // 2 metades do geodo abertas, deitadas, com o miolo de cristal entre elas
          for (const side of [-1, 1]) {
            _m.position.set(p.x + Math.cos(a) * 0.9 * s * side, 0.35 * s, p.z + Math.sin(a) * 0.9 * s * side);
            _m.rotation.set(side * 0.9, a, 0);
            _m.scale.set(s, s * 0.55, s);
            _m.updateMatrix();
            geodeHalf.add(_m, rockC.clone().multiplyScalar(0.85));
          }
          for (let j = 0; j < 5; j++) {
            _m.position.set(p.x + randRange(-0.5, 0.5) * s, 0.3 * s, p.z + randRange(-0.5, 0.5) * s);
            _m.rotation.set(randRange(-0.5, 0.5), randRange(0, 6), randRange(-0.5, 0.5));
            _m.scale.set(s * 0.8, s * randRange(1.5, 2.6), s * 0.8);
            _m.updateMatrix();
            geodeCore.add(_m, cryC);
          }
          this.addObstacle(b, p.x, p.z, 1.2 * s);
        }
      }

      // ================= RUÍNAS: colunas, arcos caídos, lajes, totens
      if (bio === "rocky" || bio === "crag" || bio === "ruin") {
        const nCol = bio === "ruin" ? 5 : 3;
        for (let k = 0; k < nCol; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          const s = randRange(0.9, 1.4);
          const h1 = randRange(2.2, 4.2) * s;
          const rot = randRange(0, 6);
          // coluna: fuste alto + toco menor deslocado no topo (topo irregular, quebrado)
          _m.position.set(p.x, h1 / 2, p.z);
          _m.rotation.set(0, rot, 0);
          _m.scale.set(s, h1, s);
          _m.updateMatrix();
          column.add(_m, rockC.clone().multiplyScalar(randRange(0.85, 1.05)));
          const h2 = randRange(0.4, 1.1) * s;
          _m.position.set(p.x + randRange(-0.2, 0.2) * s, h1 + h2 / 2 - 0.05, p.z + randRange(-0.2, 0.2) * s);
          _m.rotation.set(randRange(-0.15, 0.15), rot, randRange(-0.15, 0.15));
          _m.scale.set(s * 0.8, h2, s * 0.8);
          _m.updateMatrix();
          column.add(_m, rockC.clone().multiplyScalar(0.9));
          this.addObstacle(b, p.x, p.z, 0.75 * s);
        }
        for (let k = 0; k < 2; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          // arco caído: viga longa inclinada apoiada num bloco
          const a = randRange(0, Math.PI * 2);
          const len = randRange(3.5, 5.5);
          _m.position.set(p.x, 0.9, p.z);
          _m.rotation.set(0, a, 0.28, "YXZ"); // inclina e depois gira (ordem local)
          _m.scale.set(1, 1, len);
          _m.updateMatrix();
          _m.rotation.order = "XYZ"; // _m é compartilhado: restaura a ordem padrão
          beam.add(_m, rockC.clone().multiplyScalar(0.95));
          _m.position.set(p.x + Math.cos(a) * len * 0.45, 0.35, p.z - Math.sin(a) * len * 0.45);
          _m.rotation.set(0, a, 0);
          _m.scale.set(1.2, 0.9, 1.2);
          _m.updateMatrix();
          beam.add(_m, rockC.clone().multiplyScalar(0.85));
          this.addObstacle(b, p.x, p.z, 1.4);
        }
        for (let k = 0; k < small(6); k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          _m.position.set(p.x, 0.12, p.z);
          _m.rotation.set(randRange(-0.06, 0.06), randRange(0, 6), randRange(-0.06, 0.06));
          _m.scale.set(randRange(1.4, 2.6), 1, randRange(1.0, 1.8));
          _m.updateMatrix();
          slab.add(_m, rockC.clone().multiplyScalar(randRange(0.8, 1.1)));
        }
        for (let k = 0; k < 2; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          this.stackTotem(totem, p.x, p.z, randRange(0.9, 1.3), rockC);
          this.addObstacle(b, p.x, p.z, 0.8);
        }
      }

      // ================= PÂNTANO: juncos em volta dos lagos + névoa baixa
      if (bio === "marsh") {
        const reedC = lift(new THREE.Color(pal.foliage[0]), 0.16).lerp(new THREE.Color(0xb8c46a), 0.35);
        for (const pond of b.ponds) {
          if (Math.abs(pond.x - t.cx) > t.halfW || Math.abs(pond.z - t.cz) > t.halfH) continue;
          for (let j = 0; j < small(18); j++) {
            const a = randRange(0, Math.PI * 2);
            const rr = pond.r * randRange(1.05, 1.5);
            const x = pond.x + Math.cos(a) * rr, z = pond.z + Math.sin(a) * rr;
            if (!this.freeAt(x, z, clearings, b.half)) continue;
            const s = randRange(0.8, 1.4);
            _m.position.set(x, 1.1 * s, z);
            _m.rotation.set(randRange(-0.12, 0.12), randRange(0, 6), randRange(-0.12, 0.12));
            _m.scale.set(s, s, s);
            _m.updateMatrix();
            reed.add(_m, reedC.clone().multiplyScalar(randRange(0.8, 1.15)));
          }
        }
        for (let k = 0; k < 3; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          _m.position.set(p.x, randRange(0.5, 0.9), p.z);
          _m.rotation.set(-Math.PI / 2, 0, randRange(0, 6));
          _m.scale.set(randRange(14, 22), randRange(10, 16), 1);
          _m.updateMatrix();
          mist.add(_m, _c.setHex(pal.crystal).lerp(new THREE.Color(0xffffff), 0.7));
        }
      }

      // ================= TEMPESTADE: pilares de vento, rochas flutuantes, raios
      if (bio === "wind" || bio === "galewind" || bio === "storm" || bio === "thunder") {
        for (let k = 0; k < 3; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          const s = randRange(0.85, 1.25);
          this.place(pillar, p.x, 3.5 * s, p.z, s, randRange(0, 6), rockC);
          _m.position.set(p.x, 7.25 * s, p.z);
          _m.rotation.set(Math.PI / 2, 0, 0);
          _m.scale.setScalar(s);
          _m.updateMatrix();
          pillarRing.add(_m, cryC);
          this.addObstacle(b, p.x, p.z, 0.65 * s);
        }
        for (let k = 0; k < 3; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          const s = randRange(0.7, 1.4);
          const y = randRange(3, 6);
          _m.position.set(p.x, y, p.z);
          _m.rotation.set(randRange(0, 3), randRange(0, 3), randRange(0, 3));
          _m.scale.set(s, s * randRange(0.7, 1), s);
          _m.updateMatrix();
          floatRock.add(_m, rockC.clone().multiplyScalar(randRange(0.85, 1.05)));
          // sombra blob no chão (sem luz nova: um círculo escuro translúcido)
          _m.position.set(p.x, 0.04, p.z);
          _m.rotation.set(-Math.PI / 2, 0, 0);
          _m.scale.set(1.3 * s, 1.3 * s, 1);
          _m.updateMatrix();
          blob.add(_m, _c.setHex(0xffffff));
        }
        if (bio === "thunder") {
          for (let k = 0; k < 3; k++) {
            const p = this.scatterIn(t, clearings);
            if (!p) continue;
            // raio estático: plano vertical fino, 7–10 m, cruzado em X pra ler de qualquer ângulo
            const h = randRange(7, 10);
            const rot = randRange(0, Math.PI);
            for (const extra of [0, Math.PI / 2]) {
              _m.position.set(p.x, h / 2, p.z);
              _m.rotation.set(0, rot + extra, randRange(-0.12, 0.12));
              _m.scale.set(randRange(0.5, 0.9), h, 1);
              _m.updateMatrix();
              bolt.add(_m, cryC.clone().lerp(new THREE.Color(0xffffff), 0.5));
            }
          }
        }
      }

      // ================= GELO: estalagmites
      if (bio === "frost") {
        for (let k = 0; k < small(14); k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          const s = randRange(0.5, 1.6);
          _m.position.set(p.x, 1.1 * s, p.z);
          _m.rotation.set(randRange(-0.1, 0.1), randRange(0, 6), randRange(-0.1, 0.1));
          _m.scale.set(s, s * randRange(0.9, 1.5), s);
          _m.updateMatrix();
          icicle.add(_m, _c.setHex(pal.crystal).lerp(new THREE.Color(0xffffff), 0.3));
          if (s > 1.1) this.addObstacle(b, p.x, p.z, 0.4 * s);
        }
      }

      // ================= BRASA: fendas de lava e rochas escuras
      if (bio === "ember") {
        for (let k = 0; k < 4; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          _m.position.set(p.x, 0.05, p.z);
          _m.rotation.set(-Math.PI / 2, 0, randRange(0, 6));
          _m.scale.set(randRange(0.8, 1.4), randRange(4, 8), 1);
          _m.updateMatrix();
          lava.add(_m, _c.setHex(0xff7a2a).multiplyScalar(randRange(0.9, 1.2)));
        }
        for (let k = 0; k < small(10); k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          const s = randRange(0.6, 1.5);
          _m.position.set(p.x, s * 0.35, p.z);
          _m.rotation.set(randRange(0, 3), randRange(0, 3), randRange(0, 3));
          _m.scale.set(s, s * randRange(0.6, 0.9), s);
          _m.updateMatrix();
          darkRock.add(_m, DARK.clone().multiplyScalar(randRange(0.8, 1.2)));
          this.addObstacle(b, p.x, p.z, 0.7 * s);
        }
      }

      // ================= COVIL: ossos/presas e totens do Alfa
      if (bio === "den") {
        for (let k = 0; k < small(12); k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          const s = randRange(0.7, 1.5);
          const a = randRange(0, Math.PI * 2);
          // presa curva = 2 cones: um da base inclinado, outro na ponta mais inclinado ainda
          _m.position.set(p.x, 0.7 * s, p.z);
          _m.rotation.set(0, a, 0.35, "YXZ");
          _m.scale.set(s, s, s);
          _m.updateMatrix();
          bone.add(_m, BONE.clone().multiplyScalar(randRange(0.85, 1.05)));
          _m.position.set(p.x + Math.cos(a) * 0.45 * s, 1.5 * s, p.z - Math.sin(a) * 0.45 * s);
          _m.rotation.set(0, a, 0.85, "YXZ");
          _m.scale.set(s * 0.7, s * 0.7, s * 0.7);
          _m.updateMatrix();
          _m.rotation.order = "XYZ"; // _m é compartilhado: restaura a ordem padrão
          bone.add(_m, BONE.clone().multiplyScalar(0.95));
        }
        for (let k = 0; k < 2; k++) {
          const p = this.scatterIn(t, clearings);
          if (!p) continue;
          const s = randRange(1.0, 1.4);
          this.stackTotem(totem, p.x, p.z, s, lift(new THREE.Color(pal.rock), 0.14));
          // cristal do Alfa no topo
          _m.position.set(p.x, 3.4 * s, p.z);
          _m.rotation.set(0, randRange(0, 6), 0);
          _m.scale.set(0.7 * s, 1.6 * s, 0.7 * s);
          _m.updateMatrix();
          bigCrystal.add(_m, cryC);
          this.addObstacle(b, p.x, p.z, 0.8 * s);
        }
      }
    }

    for (const batch of [shroomStem, shroomCap, stump, fence, bigCrystal, crystalBase, geodeHalf, geodeCore, column, beam, slab, totem, reed, mist, pillar, pillarRing, floatRock, blob, bolt, icicle, lava, darkRock, bone]) {
      if (batch.size > 0) batch.build(b.group, b.chunks);
    }
  }

  /** 10.9BS — totem: 3 blocos empilhados, cada um girado e um pouco menor que o de baixo. */
  private stackTotem(batch: InstanceBatch, x: number, z: number, s: number, color: THREE.Color): void {
    let y = 0;
    for (let j = 0; j < 3; j++) {
      const w = (1.3 - j * 0.25) * s;
      const h = (1.0 + (j === 1 ? 0.2 : 0)) * s;
      _m.position.set(x, y + h / 2, z);
      _m.rotation.set(0, j * 0.6 + randRange(-0.2, 0.2), 0);
      _m.scale.set(w, h, w);
      _m.updateMatrix();
      batch.add(_m, color.clone().multiplyScalar(1 - j * 0.07));
      y += h;
    }
  }

  /** VIDA: sombra de nuvens, pássaros, esporos e partículas de bioma.
   *  10.9BS — vida POR REGIÃO: Floresta = vagalumes (acendem à noite), Terras Partidas = motes
   *  de cristal ciano + faíscas, Tempestade = detritos voando na horizontal. */
  private buildAmbient(b: RegionBuild, terrs: Territory[], regionId: string): void {
    b.cloudTex = this.makeCloudTexture();
    const cloudMat = new THREE.MeshBasicMaterial({ map: b.cloudTex, transparent: true, opacity: 0.2, depthWrite: false, color: 0x1a2444 });
    b.ownedMats.push(cloudMat);
    const clouds = new THREE.Mesh(new THREE.PlaneGeometry(b.half * 2 + 20, b.half * 2 + 20), cloudMat);
    clouds.rotation.x = -Math.PI / 2;
    clouds.position.y = 0.07;
    clouds.renderOrder = 2;
    b.group.add(clouds);

    // 10.9BR — DISTRIBUIÇÃO PELO MAPA REAL (`b.half`), não por ±60/±70 fixos que cobriam só o
    // miolo. A densidade é mantida (referência: 3 bandos / 40 esporos numa área de 140×140) e
    // escala com a ÁREA da região, com teto — preparo pra Fase 3.
    const areaK = (b.half * b.half) / (70 * 70);
    const birdMat = new THREE.MeshBasicMaterial({ color: 0x1b1830, transparent: true, opacity: 0.55, side: THREE.DoubleSide });
    b.ownedMats.push(birdMat);
    const birdGeo = new THREE.ConeGeometry(0.5, 1.5, 3);
    const nFlocks = Math.min(6, Math.max(3, Math.round(3 * Math.sqrt(areaK))));
    const spread = Math.max(20, b.half - 30);
    for (let f = 0; f < nFlocks; f++) {
      const flock = new THREE.Group();
      const cx = randRange(-spread, spread);
      const cz = randRange(-spread, spread);
      flock.position.set(cx, randRange(16, 26), cz);
      for (let i = 0; i < 4; i++) {
        const bird = new THREE.Mesh(birdGeo, birdMat);
        bird.position.set(randRange(-4, 4), randRange(-1.5, 1.5), randRange(-4, 4));
        bird.rotation.x = Math.PI / 2;
        bird.scale.setScalar(randRange(0.7, 1.2));
        flock.add(bird);
      }
      b.group.add(flock);
      b.flocks.push({ g: flock, r: randRange(14, 26), ph: randRange(0, 6.28), sp: randRange(0.06, 0.13), cx, cz });
    }

    // 10.9BR — esporos num InstancedMesh (antes: 40 Mesh = até 40 draw calls só de esporos)
    const nSpores = Math.min(120, Math.round(40 * areaK));
    const rnd = (n: number): { x: number; z: number }[] => {
      const pts: { x: number; z: number }[] = [];
      for (let i = 0; i < n; i++) pts.push({ x: randRange(-b.half, b.half), z: randRange(-b.half, b.half) });
      return pts;
    };
    // 10.9BS — a partícula "geral" do mapa agora tem a cara da região
    if (regionId === "forgottenForest") {
      // vagalumes: verdes e amarelos, baixos, piscando; a opacidade sobe à noite (update)
      b.fireflies.push(this.addSporeBatch(b, 0xb8ff6a, 0.055, [0.4, 1.9], rnd(Math.round(nSpores * 0.7)), 0.85, { blink: true }));
      b.fireflies.push(this.addSporeBatch(b, 0xffe07a, 0.05, [0.5, 2.2], rnd(Math.round(nSpores * 0.5)), 0.85, { blink: true }));
    } else if (regionId === "shatteredWilds") {
      // motes de cristal ciano (lentos) + faíscas (pequenas, piscando)
      this.addSporeBatch(b, 0x7fefff, 0.07, [0.6, 2.6], rnd(nSpores), 0.7);
      this.addSporeBatch(b, 0xdffaff, 0.035, [0.3, 2.0], rnd(Math.round(nSpores * 0.6)), 0.9, { blink: true });
    } else if (regionId === "tempestReach") {
      // folhas/detritos voando na horizontal (velocidade lateral com wrap no mapa)
      this.addSporeBatch(b, 0x9aa88a, 0.09, [0.8, 3.2], rnd(nSpores), 0.7, { vx: 7.5 });
      this.addSporeBatch(b, 0x6f7a6a, 0.07, [0.5, 2.4], rnd(Math.round(nSpores * 0.7)), 0.65, { vx: 10 });
    } else {
      this.addSporeBatch(b, 0xc9b6ff, 0.06, [0.6, 2.4], rnd(nSpores), 0.75);
    }

    const motes: { color: number; size: number; y: [number, number]; pts: { x: number; z: number }[] } = { color: 0xffd88a, size: 0.09, y: [0.4, 1.8], pts: [] };
    const dust: typeof motes = { color: 0xd9cfae, size: 0.07, y: [0.2, 1.2], pts: [] };
    const shards: typeof motes = { color: 0x9fe6ff, size: 0.1, y: [0.6, 2.4], pts: [] };
    for (const t of terrs) {
      const bio = t.def.biome;
      const kind =
        bio === "marsh" || bio === "fissure" ? motes
          : bio === "rocky" || bio === "crag" ? dust
            : bio === "crystal" || bio === "den" || bio === "shard" || bio === "storm" ? shards
              : null;
      if (!kind) continue;
      for (let i = 0; i < 10; i++) kind.pts.push({ x: t.cx + randRange(-t.halfW, t.halfW), z: t.cz + randRange(-t.halfH, t.halfH) });
    }
    for (const k of [motes, dust, shards]) {
      if (k.pts.length === 0) continue;
      this.addSporeBatch(b, k.color, k.size, k.y, k.pts, 0.8);
    }
  }

  /** 10.9BR — um tipo de partícula ambiente = UM InstancedMesh (matrizes atualizadas a 30 Hz). */
  private addSporeBatch(b: RegionBuild, color: number, size: number, y: [number, number], pts: { x: number; z: number }[], opacity: number, opts: SporeOpts = {}): SporeBatch {
    const n = pts.length;
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity });
    b.ownedMats.push(mat);
    const im = new THREE.InstancedMesh(new THREE.SphereGeometry(size, 5, 4), mat, n);
    const base = new Float32Array(n * 3);
    const ph = new Float32Array(n);
    const sp = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      base[i * 3] = pts[i].x;
      base[i * 3 + 1] = randRange(y[0], y[1]);
      base[i * 3 + 2] = pts[i].z;
      ph[i] = randRange(0, Math.PI * 2);
      sp[i] = randRange(0.3, 0.9);
      _m.position.set(base[i * 3], base[i * 3 + 1], base[i * 3 + 2]);
      _m.rotation.set(0, 0, 0);
      _m.scale.setScalar(1);
      _m.updateMatrix();
      im.setMatrixAt(i, _m.matrix);
    }
    im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    im.instanceMatrix.needsUpdate = true;
    // partículas se espalham pelo mapa: a esfera cobre tudo e o culling nativo não ajuda —
    // mas são poucas centenas de vértices, e é UM draw call em vez de dezenas
    im.frustumCulled = false;
    b.group.add(im);
    const batch: SporeBatch = { im, base, ph, sp, n, mat, vx: opts.vx ?? 0, blink: !!opts.blink, half: b.half, baseOpacity: opacity };
    b.spores.push(batch);
    return batch;
  }

  private makeCloudTexture(): THREE.CanvasTexture {
    const c = document.createElement("canvas");
    c.width = c.height = 256;
    const ctx = c.getContext("2d")!;
    ctx.clearRect(0, 0, 256, 256);
    for (let i = 0; i < 26; i++) {
      const x = Math.random() * 256;
      const y = Math.random() * 256;
      const r = 22 + Math.random() * 52;
      const g = ctx.createRadialGradient(x, y, r * 0.15, x, y, r);
      g.addColorStop(0, "rgba(255,255,255,0.6)");
      g.addColorStop(0.55, "rgba(255,255,255,0.28)");
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(1.6, 1.6);
    return tex;
  }

  /** Muralha do limite: perímetro RETANGULAR de rochas. */
  /**
   * 10.6K — MURALHA DE VERDADE fechando a área.
   *
   * Antes a "parede" era um cordão de pedregulhos de 2–4 m meio enterrados: de cima eles
   * leem como decoração de chão, não como limite — o Thiago jogou e não viu parede nenhuma.
   * Agora é uma muralha em três camadas, toda instanciada (3 draw calls no total):
   *
   *   1. PAREDÃO   — blocos altos (6–10 m) encostados um no outro, com inclinação para fora
   *      e alturas alternadas: de longe fecha o horizonte, de perto tem recorte de rocha.
   *   2. TALUDE    — pedras grandes na base, escondendo a linha reta onde o bloco encontra
   *      o chão (sem isso a muralha parece um muro de papelão apoiado no mapa).
   *   3. CRISTA    — fio emissivo no topo, na cor da região. É o que faz a borda ser vista
   *      de longe e diz "o mundo acaba aqui" sem precisar chegar perto.
   *
   * A colisão continua vindo dos mesmos pontos, então o Caçador PARA na muralha em vez de
   * ser segurado por um limite invisível alguns metros antes dela.
   */
  private buildBoundary(b: RegionBuild): void {
    // 10.9BQ — clones da biblioteca: a cor da muralha/crista é retintada por região (applyRegionTint)
    const rockMat = stoneMat(0x3c4354).clone();
    const talusMat = stoneMat(0x2c3242).clone();
    applyWorldStone(rockMat, 0.14); applyWorldStone(talusMat, 0.2); // 10.9HK — rocha com relevo (UV pelo mundo)
    const crestMat = energyMat(0x8ea2ff, "ring").clone();
    b.wallMats = [rockMat, talusMat];
    b.crestMat = crestMat;
    b.ownedMats.push(rockMat, talusMat, crestMat); // 10.9BR

    const r = b.half + 2.2;
    const step = 4.2; // blocos encostados: sem buraco por onde "ver o vazio"
    const pts: { x: number; z: number; nx: number; nz: number }[] = [];
    for (let v = -r; v <= r; v += step) {
      pts.push({ x: v, z: -r, nx: 0, nz: -1 }, { x: v, z: r, nx: 0, nz: 1 });
      pts.push({ x: -r, z: v, nx: -1, nz: 0 }, { x: r, z: v, nx: 1, nz: 0 });
    }

    // 10.9BR — muralha também em chunks: o lado oposto do mapa (a 400+ m) nem é desenhado
    const wall = new InstanceBatch(new THREE.BoxGeometry(1, 1, 1), rockMat, { castShadow: true });
    const talus = new InstanceBatch(new THREE.DodecahedronGeometry(1.5, 0), talusMat, { castShadow: true });
    const crest = new InstanceBatch(new THREE.BoxGeometry(1, 0.34, 1), crestMat);

    for (let i = 0; i < pts.length; i++) {
      const p = pts[i];
      const h = randRange(6.2, 10.4);
      const w = step * randRange(1.05, 1.35); // sobrepõe o vizinho: nada de fresta
      const d = randRange(2.6, 4.2);
      // empurra o bloco para FORA e inclina o topo para fora (parede de penhasco)
      const ox = p.nx * randRange(0.2, 1.1);
      const oz = p.nz * randRange(0.2, 1.1);
      const tilt = randRange(0.03, 0.11);
      _m.position.set(p.x + ox, h / 2 - 0.6, p.z + oz);
      _m.rotation.set(p.nz * tilt, randRange(-0.09, 0.09), -p.nx * tilt);
      _m.scale.set(p.nx !== 0 ? d : w, h, p.nx !== 0 ? w : d);
      _m.updateMatrix();
      wall.add(_m, _c.setHex(0xffffff).multiplyScalar(randRange(0.72, 1.15)));

      // crista luminosa acompanhando o topo do bloco
      _m.position.set(p.x + ox - p.nx * d * 0.3, h - 0.6, p.z + oz - p.nz * d * 0.3);
      // fio fino na borda INTERNA do topo (antes cobria o topo inteiro e virava um chapéu)
      _m.scale.set(p.nx !== 0 ? d * 0.26 : w * 0.9, 1, p.nx !== 0 ? w * 0.9 : d * 0.26);
      _m.rotation.set(0, 0, 0);
      _m.updateMatrix();
      crest.add(_m, _c.setHex(0xffffff));

      // talude: pedra grande na base, virada para dentro
      const ts = randRange(0.9, 2.0);
      const tx = p.x - p.nx * randRange(1.4, 3.4);
      const tz = p.z - p.nz * randRange(1.4, 3.4);
      _m.position.set(tx, randRange(-0.5, 0.9), tz);
      _m.rotation.set(randRange(0, 3), randRange(0, 3), randRange(0, 3));
      _m.scale.setScalar(ts);
      _m.updateMatrix();
      talus.add(_m, _c.setHex(0xffffff).multiplyScalar(randRange(0.7, 1.1)));

      this.addObstacle(b, p.x, p.z, Math.max(w, d) * 0.6);
    }
    for (const batch of [wall, talus, crest]) batch.build(b.group, b.chunks);
  }


  // ============================================================== update
  /**
   * Anima esporos, cristais, vento na grama, nuvens e pássaros (só a região ativa).
   * 10.9BR — `px/pz` = posição do player: liga/desliga os chunks de decoração a cada 0,5 s.
   * Vento da grama = 1 uniform. Esporos a 30 Hz. Com a aba escondida (document.hidden) as
   * partículas ambiente nem são tocadas.
   */
  update(dt: number, px = 0, pz = 0): void {
    this.tAcc += dt;
    const t = this.tAcc;
    const b = this.active;

    // culling por chunk: distância do player até a BORDA da célula (não o centro)
    this.cullAcc += dt;
    if (this.cullAcc >= 0.5) {
      this.cullAcc = 0;
      const r2 = cullDist() * cullDist();
      const h = CHUNK * 0.5;
      for (const c of b.chunks) {
        const dx = Math.max(0, Math.abs(c.cx - px) - h);
        const dz = Math.max(0, Math.abs(c.cz - pz) - h);
        c.im.visible = dx * dx + dz * dz < r2;
      }
    }

    if (b.grassTime) b.grassTime.value = t;
    const e = 0.6 + Math.sin(t * 1.4) * 0.25;
    for (const m of b.crystalMats) m.emissiveIntensity = e;
    if (b.waterMat) b.waterMat.opacity = 0.78 + Math.sin(t * 0.9) * 0.06;

    if (document.hidden) return; // partículas ambiente: nada a mostrar
    this.sporeAcc += dt;
    if (this.sporeAcc >= 1 / 30) {
      this.sporeAcc = 0;
      // 10.9BS — vagalumes: quase apagados de dia, acesos à noite
      const night = 1 - this.dayLight;
      for (const f of b.fireflies) f.mat.opacity = f.baseOpacity * (0.18 + 0.82 * night);
      for (const s of b.spores) {
        const span = s.half * 2;
        for (let i = 0; i < s.n; i++) {
          const k = t * s.sp[i] + s.ph[i];
          const i3 = i * 3;
          let x = s.base[i3] + Math.sin(k) * 0.9;
          // 10.9BS — detritos da Tempestade: deriva lateral com wrap dentro do mapa
          if (s.vx !== 0) x = ((x + t * s.vx + s.half) % span + span) % span - s.half;
          _m.position.set(x, s.base[i3 + 1] + Math.sin(k * 1.7) * 0.35, s.base[i3 + 2] + Math.cos(k * 0.8) * 0.9);
          _m.rotation.set(0, 0, 0);
          // 10.9BS — piscar: escala pulsa (vagalumes/faíscas) em vez de opacidade por instância
          _m.scale.setScalar(s.blink ? Math.max(0.05, 0.55 + Math.sin(k * 2.6) * 0.75) : 1);
          _m.updateMatrix();
          s.im.setMatrixAt(i, _m.matrix);
        }
        s.im.instanceMatrix.needsUpdate = true;
      }
    }
    if (b.cloudTex) {
      b.cloudTex.offset.x = (t * 0.006) % 1;
      b.cloudTex.offset.y = (t * 0.0035) % 1;
    }
    for (const f of b.flocks) {
      const a = t * f.sp + f.ph;
      f.g.position.x = f.cx + Math.cos(a) * f.r;
      f.g.position.z = f.cz + Math.sin(a) * f.r;
      f.g.rotation.y = -a + Math.PI / 2;
    }
  }

  // ------------------------------------------------------------- colisão
  private addObstacle(b: RegionBuild, x: number, z: number, r: number): void {
    const o = { x, z, r };
    b.obstacles.push(o);
    const c0 = Math.floor((x - r) / BUCKET), c1 = Math.floor((x + r) / BUCKET);
    const r0 = Math.floor((z - r) / BUCKET), r1 = Math.floor((z + r) / BUCKET);
    for (let cz = r0; cz <= r1; cz++) {
      for (let cx = c0; cx <= c1; cx++) {
        const key = (cz + 512) * 2048 + (cx + 512);
        let list = b.buckets.get(key);
        if (!list) { list = []; b.buckets.set(key, list); }
        list.push(o);
      }
    }
  }

  /** Empurra `pos` para fora de qualquer obstáculo (grid de buckets: só a vizinhança). */
  resolveObstacles(pos: THREE.Vector3, radius: number): void {
    const buckets = this.active.buckets;
    const c = Math.floor(pos.x / BUCKET);
    const r = Math.floor(pos.z / BUCKET);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const list = buckets.get(((r + dz) + 512) * 2048 + ((c + dx) + 512));
        if (!list) continue;
        for (const o of list) {
          const ox = pos.x - o.x;
          const oz = pos.z - o.z;
          const min = o.r + radius;
          const d2 = ox * ox + oz * oz;
          if (d2 >= min * min) continue;
          if (d2 < 1e-6) { pos.x = o.x + min; continue; }
          const d = Math.sqrt(d2);
          const push = (min - d) / d;
          pos.x += ox * push;
          pos.z += oz * push;
        }
      }
    }
  }

  /** Mantém uma posição dentro dos limites jogáveis. */
  // 10.9AM — OVERRIDE de limites: a Zona dos Bosses fica FORA do mapa (só por portal). Enquanto o
  // player está lá, o clamp usa o quadrado da ZONA em vez do mapa — senão o traria de volta.
  private clampOverride: { cx: number; cz: number; half: number } | null = null;
  setClampOverride(o: { cx: number; cz: number; half: number } | null): void {
    this.clampOverride = o;
  }

  clamp(pos: THREE.Vector3, radius: number): void {
    const o = this.clampOverride;
    if (o) {
      const l = o.half - radius;
      if (pos.x < o.cx - l) pos.x = o.cx - l;
      else if (pos.x > o.cx + l) pos.x = o.cx + l;
      if (pos.z < o.cz - l) pos.z = o.cz - l;
      else if (pos.z > o.cz + l) pos.z = o.cz + l;
      return;
    }
    // 10.6K: a muralha nasce em half+2.2; o limite fica logo dentro dela, então o Caçador
    // encosta na parede em vez de bater num limite invisível metros antes.
    const lim = this.halfSize + 0.6 - radius;
    if (pos.x < -lim) pos.x = -lim;
    else if (pos.x > lim) pos.x = lim;
    if (pos.z < -lim) pos.z = -lim;
    else if (pos.z > lim) pos.z = lim;
  }
}
